import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { CalendarModule } from 'primeng/calendar';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { GuestCardService } from './guest-card.service';
import { PropertyService } from '../../properties/service/property.service';
import { forkJoin } from 'rxjs';
import * as moment from 'moment-timezone';

@Component({
  selector: 'app-guest-cards',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    InputTextModule,
    DropdownModule,
    CalendarModule,
    ButtonModule,
    TagModule,
    TooltipModule,
  ],
  templateUrl: './guest-cards.component.html',
  styleUrls: ['./guest-cards.component.scss'],
})
export class GuestCardsComponent implements OnInit {
  guestCards: any[] = [];
  totalRecords = 0;
  propertyTimezone = 'UTC';
  rowsPerPage = 10;
  isLoading = false;

  filters: any = {
    search: '',
    message_status: null,
    prospect_source: null,
    yardi_property_source_id: null,
    dateRange: null,
  };

  messageStatusOptions = [
    { label: 'Pending', value: 'pending' },
    { label: 'Sent', value: 'sent' },
    { label: 'Failed', value: 'failed' },
  ];

  leadSourceOptions: { label: string; value: string }[] = [];
  propertySourceOptions: { label: string; value: string }[] = [];

  private lastEvent: any = null;

  constructor(
    private guestCardService: GuestCardService,
    private propertyService: PropertyService,
  ) {}

  ngOnInit(): void {
    forkJoin({
      leadSources: this.guestCardService.getLeadSources(),
      propertySources: this.guestCardService.getPropertySourceIds(),
    }).subscribe({
      next: ({ leadSources, propertySources }) => {
        this.leadSourceOptions = leadSources.map(s => ({ label: s, value: s }));
        this.propertySourceOptions = propertySources.map(s => ({ label: s, value: s }));
      },
    });

    this.propertyService.getUserInfo().subscribe(user => {
      this.propertyTimezone = user?.property?.timezone || 'UTC';
    });
  }

  loadGuestCards(event: any): void {
    this.lastEvent = event;
    const page = Math.floor(event.first / event.rows) + 1;
    const params: Record<string, any> = { page, limit: event.rows };

    if (this.filters.search) params['search'] = this.filters.search;
    if (this.filters.message_status) params['message_status'] = this.filters.message_status;
    if (this.filters.prospect_source) params['prospect_source'] = this.filters.prospect_source;
    if (this.filters.yardi_property_source_id) params['yardi_property_source_id'] = this.filters.yardi_property_source_id;

    if (this.filters.dateRange?.[0]) {
      params['date_from'] = this.filters.dateRange[0].toISOString();
      if (this.filters.dateRange[1]) {
        params['date_to'] = this.filters.dateRange[1].toISOString();
      }
    }

    this.isLoading = true;
    this.guestCardService.getGuestCards(params).subscribe({
      next: (res) => {
        this.guestCards = res.items;
        this.totalRecords = res.total;
        this.isLoading = false;
      },
      error: () => { this.isLoading = false; },
    });
  }

  onFilterChange(): void {
    if (this.lastEvent) {
      this.loadGuestCards({ ...this.lastEvent, first: 0 });
    }
  }

  onDateRangeChange(range: Date[]): void {
    // Cleared → null out and reload immediately
    if (!range || !range[0]) {
      this.filters.dateRange = null;
      this.onFilterChange();
      return;
    }
    // Only reload once both start AND end date are chosen
    if (range[1]) {
      this.onFilterChange();
    }
  }

  resetFilters(): void {
    this.filters = {
      search: '',
      message_status: null,
      prospect_source: null,
      yardi_property_source_id: null,
      dateRange: null,
    };
    this.onFilterChange();
  }

  getStatusSeverity(status: string): 'success' | 'warning' | 'danger' | 'info' {
    if (status === 'sent') return 'success';
    if (status === 'pending') return 'warning';
    if (status === 'failed') return 'danger';
    return 'info';
  }

  getFullName(card: any): string {
    const n = card?.payload?.name;
    if (!n) return '—';
    return [n.firstName, n.lastName].filter(Boolean).join(' ') || '—';
  }

  getPhone(card: any): string {
    return card?.payload?.phones?.[0]?.phoneNumber || '—';
  }

  getEmail(card: any): string {
    return card?.payload?.email || '—';
  }

  getMoveInDate(card: any): string {
    return card?.payload?.lease?.expectedMoveInDate || '—';
  }

  getDesiredUnit(card: any): string {
    return card?.payload?.preferences?.desiredUnit || '—';
  }

  getDesiredFloorplan(card: any): string {
    return card?.payload?.preferences?.desiredFloorplan || '—';
  }

  getTargetMoveIn(card: any): string {
    return card?.payload?.preferences?.targetMoveInDate || '—';
  }

  getDesiredBedrooms(card: any): string | number {
    const val = card?.payload?.preferences?.desiredNumBedroomsExact;
    return val != null && val !== 0 ? val : '—';
  }

  getDesiredRent(card: any): string {
    const val = card?.payload?.preferences?.desiredRent;
    return val != null && val !== 0 ? `$${val.toLocaleString()}` : '—';
  }

  getSyncSeverity(sync: any, hasTour: boolean): 'success' | 'warning' | 'danger' | 'secondary' {
    if (!hasTour) return 'secondary';           // No tour in Petrie at all
    if (!sync) return 'warning';               // Tour exists, sync not started
    if (sync.in_sync === true) return 'success';
    if (sync.in_sync === false) return 'danger';
    return 'secondary';
  }

  getSyncLabel(sync: any, hasTour: boolean): string {
    if (!hasTour) return 'No Tour';
    if (!sync) return 'Sync Pending';
    if (sync.in_sync === true) return 'Synced';
    if (sync.in_sync === false) return 'Out of Sync';
    return 'Unknown';
  }

  private readonly triggerLabels: Record<string, string> = {
    tour_created:               'Tour Created',
    tour_rescheduled:           'Tour Rescheduled',
    tour_cancelled:             'Tour Cancelled',
    tour_rebooked_after_cancel: 'Rebooked After Cancel',
    tour_completed:             'Tour Completed',
    tour_active:                'Tour Active',
  };

  private readonly eventTypeLabels: Record<string, string> = {
    Appointment:       'Appointment',
    CancelAppointment: 'Cancel Appointment',
    ReActivate:        'Re-Activate',
    Show:              'Show',
  };

  private readonly statusLabels: Record<string, string> = {
    success:                 'Success',
    failed_unit_unavailable: 'Failed — Unit Unavailable',
    failed_yardi_error:      'Failed — Yardi Error',
    failed_network:          'Failed — Network Error',
    failed_precondition:     'Failed — Precondition',
    skipped_no_op:           'Skipped (No Action)',
  };

  getSyncTooltip(sync: any, hasTour: boolean): string {
    const fmt = (d: any) => moment.tz(d, this.propertyTimezone).format('MM/DD/YYYY hh:mm A');
    const label = (map: Record<string, string>, key: string) => map[key] ?? key;

    // State 1: No tour in Petrie for this prospect
    if (!hasTour) {
      return 'No tour has been created in Petrie for this prospect.\nNo sync with Yardi has occurred.';
    }

    // State 2: Tour exists in Petrie but push has never run (yardi_guest_card_sync is null)
    if (!sync) {
      return '⚠ A tour exists in Petrie but has not been synced to Yardi yet.\nThe system will attempt sync within the next 5 minutes.';
    }

    // State 3: Synced — show last successful push details
    if (sync.in_sync === true) {
      const lines = ['✓ Tour is synced with Yardi.'];
      if (sync.last_successful_trigger)
        lines.push(`Trigger: ${label(this.triggerLabels, sync.last_successful_trigger)}`);
      if (sync.last_successful_event_type)
        lines.push(`Yardi Event: ${label(this.eventTypeLabels, sync.last_successful_event_type)}`);
      if (sync.last_successful_at)
        lines.push(`Synced At: ${fmt(sync.last_successful_at)}`);
      return lines.join('\n');
    }

    // State 4: Out of sync — push was attempted but failed or tour has changed since last push
    const lines = ['✕ Tour is out of sync with Yardi.'];
    if (sync.last_attempt_trigger)
      lines.push(`Trigger: ${label(this.triggerLabels, sync.last_attempt_trigger)}`);
    if (sync.last_attempt_status)
      lines.push(`Failure Reason: ${label(this.statusLabels, sync.last_attempt_status)}`);
    if (sync.last_attempt_at)
      lines.push(`Last Attempted: ${fmt(sync.last_attempt_at)}`);
    if (sync.last_error)
      lines.push(`Error: ${sync.last_error}`);
    lines.push('The system retries automatically every 5 minutes.');
    return lines.join('\n');
  }
}
